import { createReducer, on } from '@ngrx/store';
import { User } from '@/services/user-search.service';
import * as UsersActions from './users.actions';
import { UsersPagination } from '../../services/users.service';

export interface UsersState {
  users: User[];
  currentUser: User | null;
  pagination: UsersPagination | null;
  loading: boolean;
  error: any;
  totalRecords: number;
  currentPage: number;
  selectedUser: User | null;
  createSuccess: boolean;
  currentFilter: string | null;
}

export const initialUsersState: UsersState = {
  users: [],
  currentUser: null,
  pagination: null,
  loading: false,
  error: null,
  totalRecords: 0,
  currentPage: 0,
  selectedUser: null,
  createSuccess: false,
  currentFilter: null,
};

export const usersReducer = createReducer(
  initialUsersState,

  // Load Users
  on(UsersActions.loadUsers, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(UsersActions.loadUsersSuccess, (state, { pagination, totalItems }) => ({
    ...state,
    users: pagination.items, // Ensure the users array is updated
    loading: false,
    error: null,
    pagination: {
        ...pagination,
        total: totalItems,
      },
      totalRecords: totalItems,
      currentPage: pagination.page,
  })),

  on(UsersActions.loadUsersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Single User
  on(UsersActions.loadUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(UsersActions.loadUserSuccess, (state, { user }) => ({
    ...state,
    loading: false,
    error: null,
    selectedUser: user,
    currentUser: user,
  })),

  on(UsersActions.loadUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    selectedUser: null,
    currentUser: null,
  })),

  // Create User
  on(UsersActions.createUser, (state) => ({
    ...state,
    loading: true,
    error: null,
    createSuccess: false,
  })),

  on(UsersActions.createUserSuccess, (state, { user }) => ({
    ...state,
    users: [...state.users, user],
    loading: false,
    error: null,
    createSuccess: true,
    totalRecords: state.totalRecords + 1,
  })),

  on(UsersActions.createUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    createSuccess: false,
  })),

  // Update User
  on(UsersActions.updateUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(UsersActions.updateUserSuccess, (state, { user }) => ({
    ...state,
    users: state.users.map(u => u.id === user.id ? user : u),
    loading: false,
    error: null,
    selectedUser: state.selectedUser?.id === user.id ? user : state.selectedUser,
    currentUser: state.currentUser?.id === user.id ? user : state.currentUser,
  })),

  on(UsersActions.updateUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Delete User
  on(UsersActions.deleteUser, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(UsersActions.deleteUserSuccess, (state, { userId }) => ({
    ...state,
    users: state.users.filter(u => u.id !== userId),
    loading: false,
    error: null,
    totalRecords: state.totalRecords - 1,
    selectedUser: state.selectedUser?.id === userId ? null : state.selectedUser,
    currentUser: state.currentUser?.id === userId ? null : state.currentUser,
  })),

  on(UsersActions.deleteUserFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Clear User State
  on(UsersActions.clearUserState, () => initialUsersState)
);
